const localeCodes = ['en', 'fi', 'bn'];

function normalizePath(value = '/') {
    const path = value.split(/[?#]/)[0] || '/';
    return `/${path.replace(/^\/+|\/+$/g, '')}${
        path.replace(/\//g, '') ? '/' : ''
    }`;
}

function localeFromPath(path = '/') {
    const first = path.split('/')[1];
    return first === 'fi' || first === 'bn' ? first : 'en';
}

function unprefixedPath(path = '/') {
    return normalizePath(path.replace(/^\/(fi|bn)(?=\/|$)/, '') || '/');
}

function localePath(path, locale) {
    const base = unprefixedPath(path);
    return locale === 'en' ? base : `/${locale}${base}`;
}

function getTranslations(path, routes, sources) {
    return sources
        .map((source) => ({ ...source, path: localePath(path, source.code) }))
        .filter((source) => routes.has(source.path));
}

function isSiteUrl(url, siteUrl, sources) {
    const hosts = [
        siteUrl,
        'https://cloudoora.com',
        ...sources.map((source) => source.url),
    ].flatMap((value) => {
        try {
            return [new URL(value).hostname];
        } catch {
            return [];
        }
    });
    return /^https?:$/.test(url.protocol) && hosts.includes(url.hostname);
}

function localizeLink(value, { locale = 'en', routes, siteUrl, sources = [] }) {
    if (typeof value !== 'string' || !value || /^[?#]/.test(value))
        return value;
    let link = value.replace(/^internal:/, '');
    let url;
    try {
        if (!link.startsWith('/') && !/^https?:\/\//i.test(link)) return value;
        url = new URL(link, siteUrl || 'https://cloudoora.com');
    } catch {
        return value;
    }
    if (!isSiteUrl(url, siteUrl, sources)) return value;
    // Only actual Gatsby routes are language links. Downloads, API endpoints,
    // WordPress admin/media, and checkout URLs must retain their original URL.
    const target = localePath(url.pathname, locale);
    if (routes?.has(target)) return target + url.search + url.hash;
    const original = normalizePath(url.pathname);
    if (routes?.has(original)) return original + url.search + url.hash;
    return value;
}

function localizeData(value, settings, key = '') {
    if (key === 'generalSettings') return value;
    if (Array.isArray(value))
        return value.map((item) => localizeData(item, settings, key));
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([name, item]) => [
                name,
                localizeData(item, settings, name),
            ])
        );
    }
    // Match link fields, not media URLs, titles, schema JSON, or arbitrary text.
    if (typeof value === 'string' && /^(uri|url|link|href)$/i.test(key))
        return localizeLink(value, settings);
    return value;
}

function selectLocalizedData(data, locale) {
    return Object.fromEntries(
        Object.entries(data || {})
            .filter(([key]) => key.startsWith(`${locale}__`))
            .map(([key, value]) => [key.slice(locale.length + 2), value])
    );
}

function localizeJsonLd(value, settings) {
    if (!value) return value;
    let parsed;
    try {
        parsed = typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
        return value;
    }
    const walk = (item, key) => {
        if (Array.isArray(item)) return item.map((child) => walk(child, key));
        if (item && typeof item === 'object')
            return Object.fromEntries(
                Object.entries(item).map(([name, child]) => [
                    name,
                    walk(child, name),
                ])
            );
        if (key === 'inLanguage') return settings.locale;
        if (
            typeof item === 'string' &&
            ['url', '@id', 'item', 'mainEntityOfPage'].includes(key)
        ) {
            const localized = localizeLink(item, settings);
            return localized?.startsWith('/')
                ? new URL(localized, settings.siteUrl).href
                : localized;
        }
        return item;
    };
    return JSON.stringify(walk(parsed));
}

module.exports = {
    localeCodes,
    normalizePath,
    localeFromPath,
    unprefixedPath,
    localePath,
    getTranslations,
    isSiteUrl,
    localizeLink,
    localizeData,
    selectLocalizedData,
    localizeJsonLd,
};
